/**
 * « Règle détaillée » d'une SKILLCARD (savoir saisi par le tuteur) : règle,
 * méthode, quand l'utiliser, exemple, pièges. Partagée par la page publique de
 * la carte et la fenêtre « Cours » du player. Rien si tout est vide.
 */

import { MarkdownMath } from '@jeraisonne/ui';

export interface CardRuleSource {
  rule: string;
  method: string;
  when_to_use: string;
  example: string;
  pitfalls: string;
}

/** Parties non vides, dans l'ordre de lecture. */
export function cardRuleParts(card: CardRuleSource): [string, string][] {
  return (
    [
      ['Règle', card.rule],
      ['Méthode', card.method],
      ['Quand l’utiliser ?', card.when_to_use],
      ['Exemple', card.example],
      ['Pièges fréquents', card.pitfalls],
    ] as [string, string | null | undefined][]
  ).filter((p): p is [string, string] => typeof p[1] === 'string' && p[1].trim().length > 0);
}

/**
 * Listes, paragraphes et tableaux du markdown saisi : `prose` n'est pas branché (pas de plugin
 * typography), sans quoi la méthode « 1. … 2. … » perd ses numéros, l'exemple ses respirations et le
 * tableau de la règle ses cases.
 */
const RULE_MD = [
  'text-ink-800',
  '[&_ol]:list-decimal [&_ol]:space-y-0.5 [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:space-y-0.5 [&_ul]:pl-5',
  '[&_*+ol]:mt-1 [&_*+p]:mt-1 [&_*+ul]:mt-1 [&_*+table]:mt-1.5',
  '[&_table]:w-full [&_table]:border-collapse [&_table]:text-sm',
  '[&_th]:border-ink-200 [&_th]:border-b [&_th]:px-1.5 [&_th]:py-1 [&_th]:text-left [&_th]:align-bottom [&_th]:font-semibold',
  '[&_td]:border-ink-100 [&_td]:border-b [&_td]:px-1.5 [&_td]:py-1 [&_td]:align-top',
].join(' ');

export function CardRuleDetails({ card }: { card: CardRuleSource }) {
  const parts = cardRuleParts(card);
  if (parts.length === 0) return null;
  return (
    <div className="space-y-3">
      {parts.map(([label, value]) => (
        <div key={label}>
          <p className="text-ink-500 text-xs font-semibold uppercase">{label}</p>
          {/* Saisie ligne par ligne : un retour à la ligne reste un retour à la ligne. */}
          <MarkdownMath className={RULE_MD} breaks>
            {value}
          </MarkdownMath>
        </div>
      ))}
    </div>
  );
}
